<template>
  <EInstantAnswer
    v-if="isValid"
    class="word-definitions"
    :sources="sources"
    :feedback-link="{
      ticketSubject: 'Search - Word Definitions',
      widgetType: 'word_definitions_rich'
    }"
    :sr-title="$t('web.widgets.worddefinitions.title')"
    data-track-id="word-definitions"
    data-test-id="word-definitions"
    powered-by-ecosia
  >
    <EHeading as="h3" class="word-definitions__headword">
      {{ headword }}
      <span class="word-definitions__labels">
        <span
          v-for="label in labels"
          :key="label"
          class="word-definitions__label"
        >
          {{ label }}
        </span>
      </span>
    </EHeading>
    <div
      v-if="ipaPronunciation"
      class="word-definitions__pronunciation"
      aria-hidden="true"
    >
      {{ ipaPronunciation }}
    </div>
    <dl class="word-definitions__list">
      <div
        v-for="group in definitionGroups.slice(0, maxDefinitionGroups)"
        :key="group.partOfSpeech"
        class="word-definitions__group"
        data-test-id="word-definitions-group"
      >
        <dt class="word-definitions__pos">
          {{ group.partOfSpeech }}
          <span class="word-definitions__labels">
            <span
              v-for="label in group.labels"
              :key="label"
              class="word-definitions__label"
            >
              {{ label }}
            </span>
          </span>
        </dt>
        <dd
          v-for="(def, index) in getDefinitions(group)"
          :key="index"
          class="word-definitions__definition"
          data-test-id="word-definitions-definition"
        >
          <span v-safe-html="def.definition" />
          <span class="word-definitions__labels">
            <span
              v-for="label in def.labels"
              :key="label"
              class="word-definitions__label"
            >
              {{ label }}
            </span>
          </span>
        </dd>
      </div>
    </dl>
  </EInstantAnswer>
</template>

<script>
import { mapState } from 'vuex';

import { trackImpression, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import EHeading from '~~/common/vue/components/heading';
import EInstantAnswer from '~/components/instant-answers/base';

const isDefinitionGroup = (group) => typeof group.partOfSpeech === 'string' &&
  Array.isArray(group.definitions) &&
  typeof group.definitions[0]?.definition === 'string';

export const MAX_DEF_GROUPS_DESKTOP = 3;
export const MAX_DEF_GROUPS_MOBILE = 2;
export const MAX_DEFS_DESKTOP = 5;
export const MAX_DEFS_MOBILE = 3;
export const TRACK_ID = 'word_definitions';

export default {
  name: 'EWordDefinitions',
  components: {
    EHeading,
    EInstantAnswer,
  },
  props: {
    headword: {
      type: String,
      default: '',
    },
    definitionGroups: {
      type: Array,
      default: () => [],
      validator: (groups) => groups.every(isDefinitionGroup),
    },
    ipaPronunciation: {
      type: String,
      default: '',
    },
    labels: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState('user', ['deviceType']),
    isValid() {
      return Boolean(this.headword) && this.definitionGroups.length > 0;
    },
    isDesktop() {
      return this.deviceType === 'pc';
    },
    maxDefinitionGroups() {
      return this.isDesktop ? MAX_DEF_GROUPS_DESKTOP : MAX_DEF_GROUPS_MOBILE;
    },
    maxDefinitions() {
      return this.isDesktop ? MAX_DEFS_DESKTOP : MAX_DEFS_MOBILE;
    },
    sources() {
      return [
        {
          title: 'Oxford Languages',
          url: 'https://languages.oup.com/',
        },
      ];
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
      once: false,
    });
  },
  methods: {
    getDefinitions(group) {
      return group.definitions
        .slice(0, this.maxDefinitions)
        .filter(({ definition }) => !!definition);
    },
  },
};
</script>

<style lang="scss" scoped>
.word-definitions__headword {
  display: flex;
}

.word-definitions__pronunciation {
  display: inline-flex;

  &::before,
  &::after {
    content: "/";
  }
}

.word-definitions__list {
  margin: $space-l 0 0;
}

.word-definitions__group {
  & + & {
    margin-top: $space-s;
  }
}

.word-definitions__pos,
.word-definitions__source {
  white-space: nowrap;
}

.word-definitions__pos {
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  margin-bottom: $space-1s;
}

.word-definitions__definition {
  display: list-item;
  list-style: decimal outside;
  margin-left: $space-1l;

  & + & {
    margin-top: $space-2s;
  }
}

.word-definitions__button {
  display: flex;
  width: min-content;
  margin: $space-l auto 0;
}

.word-definitions__labels {
  display: inline-flex;
  align-items: center;

  .word-definitions__headword &,
  .word-definitions__pos & {
    margin-left: $space-2s;
  }
}

.word-definitions__label {
  color: var(--color-text-secondary);
  font-weight: $font-weight-m;
  font-size: $font-s;

  &::before {
    content: '\0000a0\0000b7';
  }
}
</style>
